import { getFingerprint, setOption } from '@thumbmarkjs/thumbmarkjs';

/**
 * Generate a unique fingerprint for the user
 * Uses thumbmarkjs for comprehensive fingerprinting with fallback
 * Always generates fresh fingerprint for security (no caching)
 * @returns {Promise<string>} Unique fingerprint hash
 */
export async function generateFingerprint() {
  // Always generate fresh fingerprint - don't use cache to prevent bypass
  
  let fingerprint;
  
  try {
    // Configure thumbmarkjs to exclude problematic components
    setOption('exclude', [
      /* Safari private mode have these data different */
      'audio.sampleHash',
      'canvas.commonImageDataHash',
      /* Chrome spectacular loading sometimes get timeout for permissions */
      'permissions'
    ]);
    
    // Primary method: thumbmarkjs comprehensive fingerprinting
    fingerprint = await getFingerprint();
  } catch (error) {
    console.warn('Thumbmarkjs fingerprinting failed, using fallback:', error);
    // Fallback method: basic screen + timezone fingerprinting
    fingerprint = generateFallbackFingerprint();
  }
  
  // DO NOT cache fingerprint to prevent bypass via sessionStorage manipulation
  return fingerprint;
}

/**
 * Generate a fallback fingerprint using basic browser information
 * @returns {string} Basic fingerprint hash
 */
function generateFallbackFingerprint() {
  if (typeof window === 'undefined') {
    // Return a default value for SSR
    return 'ssr-fallback';
  }
  
  try {
    const components = [
      // Screen resolution
      `${window.screen.width}x${window.screen.height}`,
      // Screen color depth
      window.screen.colorDepth.toString(),
      // Timezone
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Language
      navigator.language,
      // User agent (shortened to avoid too much detail)
      navigator.userAgent.slice(0, 100),
      // Platform (using userAgentData if available, otherwise userAgent)
      navigator.userAgentData?.platform || navigator.userAgent.match(/\(([^)]+)\)/)?.[1] || 'unknown',
      // Hardware concurrency (number of CPU cores)
      navigator.hardwareConcurrency?.toString() || '0'
    ];
    
    // Create a simple hash from the components
    const combined = components.join('|');
    return btoa(combined).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
  } catch (error) {
    console.warn('Fallback fingerprinting failed:', error);
    // Ultimate fallback: random string that changes each session
    return 'fallback-' + Math.random().toString(36).substring(2, 18);
  }
}

/**
 * Clear cached fingerprint (useful for testing or when user logs in)
 * Note: This function is kept for backwards compatibility but no longer caches fingerprints
 */
export function clearCachedFingerprint() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('user_fingerprint');
  }
}